import { LabelChipWithCategoryIcon } from '@modules/common/components/LabelChip';
import { AudioSummaryActions } from '@modules/feedback/components/AudioSummaryActions';
import { KeywordOrReasonSummaryListItem } from '@modules/feedback/components/KeywordsAndReasonsSummary/KeywordOrReasonSummaryListItem';
import { SummaryOrFeedbackLabel } from '@modules/feedback/components/SummaryOrFeedbackLabel';
import { trackCopyFeedbackContentEvents } from '@modules/feedback/utils/trackCopyFeedbackContentEvents';
import { List, Paper, Stack, Typography } from '@mui/material';
import { SummaryMessageUnit, SummaryMetadata } from 'generated/graphql';
import { useCallback, useState } from 'react';
import {
  Feedback,
  FeedbackContentType,
  FeedbackLabel,
} from 'v2/core/entities/feedback';
import { ReasonOrEntityLabelType } from 'v2/core/entities/feedback/labels';
import getFieldNameToDisplay from 'v2/views/components/charts/tooltip/getFieldNameToDisplay';

interface KeywordOrReasonSummaryListProps {
  label: ReasonOrEntityLabelType;
  summaryUnits: SummaryMessageUnit[] | undefined;
  shouldAnimate?: (metadata: SummaryMetadata | undefined) => boolean;
  onClick: (metadata: SummaryMetadata | undefined) => void;
  selectedLabels: FeedbackLabel[] | undefined;
  feedback: Feedback;
}

export const KeywordOrReasonSummaryList = ({
  label,
  onClick,
  shouldAnimate,
  selectedLabels,
  summaryUnits,
  feedback,
}: KeywordOrReasonSummaryListProps) => {
  const [hoveredSummary, setHoveredSummary] = useState<string | null>(null);

  const renderActions = useCallback(
    (summary: SummaryMessageUnit) => (
      <AudioSummaryActions
        feedback={feedback}
        selectedLabels={selectedLabels}
        summary={summary}
        labelID={label.id}
        summaryIndex={summaryUnits?.indexOf(summary) ?? 0}
      />
    ),
    [feedback, label.id, selectedLabels, summaryUnits],
  );

  return (
    <Paper
      variant="outlined"
      sx={{
        mb: 4,
        borderRadius: 2,
        p: 2,
        backgroundColor: 'feedback.summaryCard',
        backgroundImage: 'none',
      }}
    >
      <Stack spacing={1}>
        <SummaryOrFeedbackLabel
          isAudioRecord={feedback.content.type === FeedbackContentType.Audio}
          feedbackID={feedback.id}
          isSummaryView
          label={
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="body2">
                Summary for {getFieldNameToDisplay(label.type)}
              </Typography>
              <LabelChipWithCategoryIcon label={label} />
            </Stack>
          }
          bgVariant="transparent"
          padding={0}
        />
        <List
          dense
          sx={{ py: 0, pl: 4 }}
          onCopy={() => {
            trackCopyFeedbackContentEvents({
              eventName: 'Copied Text From Feedback Content',
              feedbackID: feedback.id,
              feedbackType: feedback.content.type,
              contentType: 'summary',
            });
          }}
        >
          {summaryUnits?.length ? (
            summaryUnits.map((summary) => (
              <KeywordOrReasonSummaryListItem
                key={summary.text}
                summary={summary}
                hoveredSummary={hoveredSummary}
                setHoveredSummary={setHoveredSummary}
                onClick={onClick}
                shouldAnimate={shouldAnimate}
                renderActions={renderActions}
                feedback={feedback}
                labelID={label.id}
                summaryIndex={summaryUnits.indexOf(summary)}
              />
            ))
          ) : (
            <Typography variant="body2" color="text.secondary" px={1} pt={1}>
              No summary found.
            </Typography>
          )}
        </List>
      </Stack>
    </Paper>
  );
};
